import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { t } from "i18next";
import cx from "classnames";
import s from "./editOrder.scss";
import Input from "Components/input/Input";
import Button from "Components/button/Button";
import QboxmailApi from "../../../qboxmailApi";
import isFQDN from "validator/lib/isFQDN";
import HelpTooltip from "Components/helpTooltip/HelpTooltip";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import { Icon, less, plus, upgradePlan, upgradeAccounts } from "Icons/index";
import TranslatedString from "Components/TranslatedString";
import currency from "currency.js";
import WarningBanner from "Components/warningBanner/WarningBanner";

const ContextTypes = {
  router: PropTypes.object,
  isMobileDevice: PropTypes.func,
  isMediaMobile: PropTypes.func,
  isMediaSM: PropTypes.func,
  isMediaMD: PropTypes.func,
  isMediaLG: PropTypes.func
};

export default class EditOrder extends React.Component {
  constructor(props) {
    super(props);

    let doms = [
      {
        code: `new_domain_1`, // fake code used only as references here in this component
        ordered: true,
        name: "",
        new_plan: "basic",
        plan: {},
        exist: false,
        new_8_count: 1,
        new_25_count: 0,
        new_50_count: 0,
        new_archive_count: 0,
        no_email_accounts: 0,
        no_email_accounts_25gb: 0,
        no_email_accounts_archive: 0
      }
    ];

    if (props.isUpgradingDomain) {
      doms = props.upgradingDomains.map(d => {
        const isOrdered = !d.code; // use this to differentiate ordered from already created

        return {
          ...d,
          new_plan: isOrdered ? d.plan : d.plan.name,
          new_8_count: d.new_8_count || 0,
          new_25_count: d.new_25_count || 0,
          new_50_count: d.new_50_count || 0,
          new_archive_count: d.new_archive_count || 0,
          ordered: isOrdered
        };
      });
    }

    let action = null;
    if (props.isAddingDomain) action = "add_domain";
    // if is already enterprise, it cannot upgrade the plan
    if (props.isUpgradingDomain && doms[0].new_plan === "enterprise") action = "add_emails";

    this.state = {
      domains: doms,
      action: action,
      notVerified: false,
      validatingDomain: false
    };
  }

  componentDidMount() {
    if (this.props.isAddingDomain) {
      const estimate = this.estimateDomain(this.state.domains[0]);
      this.updateDomain(this.state.domains[0].code, { estimate: estimate });
    }
  }

  addDomain = async () => {
    const lastDomain = this.state.domains[this.state.domains.length - 1];
    const nextID = parseInt(lastDomain.code.replace("new_domain_", ""), 10) + 1;

    if (lastDomain && !lastDomain.valid) {
      // if validation has not been triggered, trigger it now
      let res;
      if (lastDomain.valid === undefined) res = await this.validateDomain(lastDomain);
      if (lastDomain.valid === false || !res) return;
    }

    const domains = this.state.domains;
    domains.push({
      code: `new_domain_${nextID}`,
      name: "",
      new_plan: "basic",
      plan: {},
      exist: false,
      new_8_count: 1,
      new_25_count: 0,
      new_50_count: 0,
      new_archive_count: 0,
      no_email_accounts: 0,
      no_email_accounts_25gb: 0,
      no_email_accounts_archive: 0,
      estimate: 1 * this.props.prices.annually[`basic_8`]
    });

    this.setState({ domains });
  };

  goToNextStep = async () => {
    this.setState({ submitting: true });
    const valid = await this.validate();
    if (!valid) {
      this.setState({ submitting: false });
      return;
    }

    this.props.setUpgradingDomains(
      this.state.domains,
      this.props.isUpgradingDomain,
      this.state.action
    );

    if (this.props.isUpgradingDomain && !this.state.estimate) {
      await this.calcEstimateWHMCS(this.state.domains[0]);
    }

    this.setState({ submitting: false });

    this.props.setEstimate(this.state.estimate);
    this.props.goToStep(3);
  };

  validate = async () => {
    if (this.props.isUpgradingDomain) return true;

    let totalEmails = 0;
    let notVerified = false;

    for (let d of this.state.domains) {
      if (!d.valid) {
        notVerified = true;
        const res = await this.validateDomain(d);
        if (!res) return false;
      }

      totalEmails += d.new_8_count + d.new_25_count + d.new_50_count;
    }

    this.setState({
      totalEmails: totalEmails,
      notVerified: notVerified
    });

    if (notVerified) return false;
    if (totalEmails < 5) return false;

    return true;
  };

  generateDomainPlanSelector = d => {
    const isUpgradingDomain = this.props.isUpgradingDomain;
    const currentPlan = d.ordered ? d.plan : d.plan.name;

    return (
      <div className={s.planSelector}>
        <div
          onClick={() => this.changeDomainPlan(d, "basic")}
          className={cx({
            [s.selected]: d.new_plan === "basic",
            [s.disabled]: isUpgradingDomain && currentPlan !== "basic"
          })}
        >
          Basic
        </div>
        <div
          onClick={() => this.changeDomainPlan(d, "professional")}
          className={cx({
            [s.selected]: d.new_plan === "professional",
            [s.disabled]: isUpgradingDomain && currentPlan === "enterprise"
          })}
        >
          Professional
        </div>
        <div
          onClick={() => this.changeDomainPlan(d, "enterprise")}
          className={cx({ [s.selected]: d.new_plan === "enterprise" })}
        >
          Enterprise
        </div>
      </div>
    );
  };

  changeDomainPlan = (domain, plan) => {
    this.setState({ estimate: null });

    let d = { ...domain, new_plan: plan };

    let estimate;
    if (this.props.isAddingDomain) {
      estimate = this.estimateDomain(d);
    }

    this.updateDomain(domain.code, { ...d, estimate });
  };

  updateDomainName = (dom, value) => {
    this.updateDomain(dom.code, { name: value, valid: undefined });
  };

  updateDomain = (code, values) => {
    let domains = [];

    this.state.domains.forEach(d => {
      if (d.code === code) {
        d = { ...d, ...values };
      }

      domains.push({ ...d });
    });

    this.setState({ domains });
  };

  estimateDomain(dom) {
    const { prices } = this.props;

    const price8 = prices.annually[`${dom.new_plan}_8`];
    const price25 = prices.annually[`${dom.new_plan}_25`];
    const priceVault = prices.annually[`${dom.new_plan}_vault`];

    const partial8 = currency(price8).multiply(dom.new_8_count);
    const partial25 = currency(price25).multiply(dom.new_25_count);
    const partial50 = currency(price25).multiply(dom.new_50_count);
    const partialVault = currency(priceVault).multiply(dom.new_archive_count);

    const estimate = currency(partial8)
      .add(partial25)
      .add(partial50)
      .add(partialVault)
      .format();

    if (this.props.isAddingDomain) {
      return estimate;
    } else {
      this.setState({ estimate: estimate });
    }
  }

  updateResourceCount = (name, type, count, validate = true) => {
    // reset estimate that is not valid anymore
    if (this.state.estimate) this.setState({ estimate: null });

    let domains = [];

    let key = "new_8_count";
    if (type === "archive") key = "new_archive_count";
    if (type === "25") key = "new_25_count";
    if (type === "50") key = "new_50_count";

    this.state.domains.forEach(d => {
      if (d.name === name || d.code === name) {
        d[key] = count.length === 0 ? 0 : parseInt(count, 10);
      }

      if (validate) {
        // avoid negative number
        if (d[key] < 0) d[key] = 0;

        // avoid unexpected cases
        if (isNaN(d[key])) d[key] = 0;
      }

      // cannot have more archive options of the total email accounts
      let totalEmails = d.new_8_count + d.new_25_count + d.new_50_count;
      if (d.new_archive_count > totalEmails) {
        d.new_archive_count = totalEmails;
      }

      if (this.props.isAddingDomain) {
        d.estimate = this.estimateDomain(d);
      }

      domains.push({ ...d });
    });

    this.setState({ domains });
  };

  validateDomain = async d => {
    this.setState({ validatingDomain: true });

    if (!d.name || d.name.length < 3 || !isFQDN(d.name)) {
      this.updateDomain(d.code, { valid: false });
      this.setState({ validatingDomain: false });
      return false;
    } else {
      // check in the others that it is adding
      let times = 0;
      this.state.domains.forEach(dom => {
        if (d.name === dom.name) times++;
      });

      if (times > 1) {
        this.updateDomain(d.code, { valid: false });
        this.setState({ validatingDomain: false });
        return false;
      }

      // check already existing domains
      let response;

      try {
        const api = new QboxmailApi({ useCookie: true });
        response = await api.validateDomainName(d.name);
      } catch (e) {
        this.updateDomain(d.code, { valid: false });
        this.setState({ validatingDomain: false });
      }

      if (response.valid) {
        this.updateDomain(d.code, { valid: true });
      } else {
        this.updateDomain(d.code, { valid: false });
      }

      this.setState({ validatingDomain: false });

      return response.valid;
    }
  };

  handleArrows = (d, type, e) => {
    let count = d[`new_${type}_count`];

    if (e.which !== 38 && e.which !== 40) return;

    if (e.which === 38) count += 1;
    if (e.which === 40) count -= 1;

    this.updateResourceCount(d.code, type, count);
  };

  validateResourceCount = f => {};

  removeDomain = code => {
    let domains = [];
    this.state.domains.forEach(d => {
      if (d.code !== code) {
        domains.push({ ...d });
      }
    });

    this.setState({ domains });
  };

  calcEstimateWHMCS = async d => {
    this.setState({ esitmating: true });
    const data = {
      max_email_accounts: this.state.domains[0].new_8_count,
      max_email_accounts_25gb: this.state.domains[0].new_25_count,
      max_email_accounts_50gb: this.state.domains[0].new_50_count,
      max_email_accounts_archive: this.state.domains[0].new_archive_count,
      plan: this.state.domains[0].new_plan,
      domain_name: d.name,
      name: d.name,
      action: this.state.action,
      calconly: true
    };

    const api = new QboxmailApi({ useCookie: true });
    const resp = await api.upgradeAnnuallyProduct(data);

    let estimate = resp.total;
    if (estimate) estimate = estimate.replace("EUR", "").replace("€", "");

    this.setState({ esitmating: false, estimate: estimate });
  };

  setAction = action => {
    this.setState({ action });
    this.props.goToStep(2);
  };

  render() {
    const { isUpgradingDomain, prices, isAddingDomain } = this.props;
    const { domains, action, estimate, totalEmails, notVerified } = this.state;

    let disabledButton;

    if (action === "plan_upgrade") {
      let plan = domains[0].ordered ? domains[0].plan : domains[0].plan.name;

      disabledButton = plan === domains[0].new_plan;
    }

    if (action === "add_emails") {
      let total =
        domains[0].new_8_count +
        domains[0].new_25_count +
        domains[0].new_50_count +
        domains[0].new_archive_count;

      disabledButton = total === 0;
    }

    return (
      <div className="orderPage">
        {React.cloneElement(this.props.stepper, {
          ...this.props.stepper.props,
          goBack: () => {
            if (this.state.action) {
              this.props.goToStep(1);
              this.setState({ action: null });
            }
          }
        })}
        <div className={cx("sectionTitle", "titleFP")}>
          {isAddingDomain && t("order.add.title.step5")}
          {!isAddingDomain &&
            action !== "plan_upgrade" &&
            action !== "add_emails" &&
            t("orders.show.step1.title")}
          {!isAddingDomain && action === "plan_upgrade" && t("orders.show.step1.title.plan")}
          {!isAddingDomain && action === "add_emails" && t("orders.show.step1.title.emails")}
        </div>

        <div className="wrapScrollFP">
          <div className="infoFP">
            <div className="sectionDesc">
              {isAddingDomain && t("order.add.desc.step5.annual")}
              {!isAddingDomain &&
                action !== "plan_upgrade" &&
                action !== "add_emails" &&
                t("orders.show.step1.desc")}
              {!isAddingDomain && action === "plan_upgrade" && t("orders.show.step1.desc.plan")}
              {!isAddingDomain && action === "add_emails" && t("orders.show.step1.desc.emails")}
            </div>
          </div>

          <div className="wrapFormsFP">
            <div className="formsFP">
              {!this.state.action && (
                <div className={s.wrapAction}>
                  <div className={s.labelActionSelector} htmlFor={name}>
                    {t("orders.show.calc.action.question")}
                  </div>
                  <div className={s.actionSelector}>
                    <div className={s.action} onClick={() => this.setAction("plan_upgrade")}>
                      <Icon svg={upgradePlan} width="6rem" height="6rem" />
                      <div className={s.labelAction}>{t("button.upgradePlan")}</div>
                    </div>
                    <div className={s.action} onClick={() => this.setAction("add_emails")}>
                      <Icon svg={upgradeAccounts} width="6rem" height="6rem" />
                      <div className={s.labelAction}>{t("button.upgradeAccounts")}</div>
                    </div>
                  </div>
                </div>
              )}

              {this.state.action && isUpgradingDomain && (
                <div className={cx("inputFull2")}>
                  <WarningBanner
                    info
                    color="blue"
                    msg={
                      action === "plan_upgrade"
                        ? t("infoMsg.downgradePlan")
                        : action === "add_emails"
                        ? t("infoMsg.downgradeEmails")
                        : ""
                    }
                  />
                </div>
              )}

              {this.state.action &&
                this.state.domains.map((d, idx) => {
                  return (
                    <React.Fragment key={isAddingDomain ? d.code : idx}>
                      <div
                        key={isAddingDomain ? d.code : idx}
                        className={cx("inputFull2", s.domainOrderBox)}
                      >
                        <Input
                          className={s.nameDomain}
                          name={d.code}
                          label={t("input.label.domain.name")}
                          value={d.name}
                          onChange={val => this.updateDomainName(d, val[d.code])}
                          autocomplete={false}
                          required
                          onBlur={() => this.validateDomain(d)}
                          errors={d.valid === false && t("input.error.notValid.domain")}
                          disabled={isUpgradingDomain}
                          helpTooltip={
                            <HelpTooltip>
                              {[
                                {
                                  description: isUpgradingDomain
                                    ? t("helpT.order.domain.exist")
                                    : t("helpT.order.domain.add")
                                }
                              ]}
                            </HelpTooltip>
                          }
                        />

                        {this.state.action === "add_emails" && (
                          <div className={s.infoMsgDomainBox}>
                            <div className={s.labelMsgDomainBox}>
                              <TranslatedString>order.counter.addAccounts</TranslatedString>
                            </div>
                          </div>
                        )}

                        {(this.state.action === "plan_upgrade" ||
                          this.state.action === "add_domain") && (
                          <div className={cx(s.planAnnual)}>
                            <div className={s.wrapLabelPlaneAnnual}>
                              <label className={s.labelPlaneAnnual} htmlFor={name}>
                                {t("input.label.select.plan")}
                              </label>

                              <div className={s.helpTooltip}>
                                <HelpTooltip>
                                  {[
                                    {
                                      title: t("helpT.order.plan.basic.title"),
                                      description: t("helpT.order.plan.basic.desc")
                                    },
                                    {
                                      title: t("helpT.order.plan.professional.title"),
                                      description: t("helpT.order.plan.professional.desc")
                                    },
                                    {
                                      title: t("helpT.order.plan.enterprise.title"),
                                      description: t("helpT.order.plan.enterprise.desc")
                                    }
                                  ]}
                                </HelpTooltip>
                              </div>
                            </div>
                            {this.generateDomainPlanSelector(d)}
                          </div>
                        )}

                        {(this.state.action === "add_emails" ||
                          this.state.action === "add_domain") && (
                          <React.Fragment>
                            <div className={s.wrapCounter}>
                              <div className={s.wrapLabelCounter}>
                                <div className={s.labelCounter}>
                                  {t("order.counter.emailAccout.8GB")}
                                </div>
                                <div className={s.hintLabelCounter}>
                                  {prices.annually[`${d.new_plan}_8`]} € account /{" "}
                                  {t("order.counter.price.annual")}
                                </div>
                              </div>
                              {/* 8 gb email counters */}
                              <div className={s.wrapInputCounter}>
                                <div className={s.resourceCounter}>
                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={less}
                                    onClick={() =>
                                      this.updateResourceCount(d.name, "8", d.new_8_count - 1)
                                    }
                                    hoverSmall
                                  />
                                  <input
                                    onKeyDown={e => this.handleArrows(d, "8", e)}
                                    onBlur={() => this.validateResourceCount("8")}
                                    value={d.new_8_count}
                                    onChange={e =>
                                      this.updateResourceCount(
                                        d.name,
                                        "8",
                                        e.currentTarget.value,
                                        false
                                      )
                                    }
                                  />

                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={plus}
                                    onClick={() =>
                                      this.updateResourceCount(d.name, "8", d.new_8_count + 1)
                                    }
                                    hoverSmall
                                  />
                                </div>
                              </div>
                            </div>
                            <div className={s.wrapCounter}>
                              <div className={s.wrapLabelCounter}>
                                <div className={s.labelCounter}>
                                  {t("order.counter.emailAccout.25GB")}
                                </div>
                                <div className={s.hintLabelCounter}>
                                  {prices.annually[`${d.new_plan}_25`]} € account /{" "}
                                  {t("order.counter.price.annual")}
                                </div>
                              </div>
                              {/* 25 gb email counters */}
                              <div className={s.wrapInputCounter}>
                                <div className={s.resourceCounter}>
                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={less}
                                    onClick={() =>
                                      this.updateResourceCount(d.name, "25", d.new_25_count - 1)
                                    }
                                    hoverSmall
                                  />
                                  <input
                                    onKeyDown={e => this.handleArrows(d, "25", e)}
                                    onBlur={() => this.validateResourceCount("25")}
                                    value={d.new_25_count}
                                    onChange={e =>
                                      this.updateResourceCount(
                                        d.name,
                                        "25",
                                        e.currentTarget.value,
                                        false
                                      )
                                    }
                                  />
                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={plus}
                                    onClick={() =>
                                      this.updateResourceCount(d.name, "25", d.new_25_count + 1)
                                    }
                                    hoverSmall
                                  />
                                </div>
                              </div>
                            </div>

                            {d.new_plan !== "basic" && (
                              <div className={s.wrapCounter}>
                                <div className={s.wrapLabelCounter}>
                                  <div className={s.labelCounter}>
                                    {t("order.counter.emailAccount.50GB")}
                                  </div>
                                  <div className={s.hintLabelCounter}>
                                    {prices.annually[`${d.new_plan}_50`]} € account /{" "}
                                    {t("order.counter.price.annual")}
                                  </div>
                                </div>
                                {/* 50 gb email counters */}
                                <div className={s.wrapInputCounter}>
                                  <div className={s.resourceCounter}>
                                    <ButtonIcon
                                      className={s.btnResourceCounter}
                                      icon={less}
                                      onClick={() =>
                                        this.updateResourceCount(d.name, "50", d.new_50_count - 1)
                                      }
                                      hoverSmall
                                    />
                                    <input
                                      onKeyDown={e => this.handleArrows(d, "50", e)}
                                      onBlur={() => this.validateResourceCount("50")}
                                      value={d.new_50_count}
                                      onChange={e =>
                                        this.updateResourceCount(
                                          d.name,
                                          "50",
                                          e.currentTarget.value,
                                          false
                                        )
                                      }
                                    />
                                    <ButtonIcon
                                      className={s.btnResourceCounter}
                                      icon={plus}
                                      onClick={() =>
                                        this.updateResourceCount(d.name, "50", d.new_50_count + 1)
                                      }
                                      hoverSmall
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className={s.infoMsgDomainBox}>
                              <div className={s.labelMsgDomainBox}>
                                <TranslatedString>order.counter.paidOptions</TranslatedString>
                              </div>
                            </div>
                            <div className={s.wrapCounter}>
                              <div className={s.wrapLabelCounter}>
                                <div className={s.labelCounter}>{t("order.counter.archive")}</div>
                                <div className={s.hintLabelCounter}>
                                  {prices.annually[`${d.new_plan}_vault`]} € account /{" "}
                                  {t("order.counter.price.annual")}
                                </div>
                              </div>
                              {/* archive email counters */}
                              <div className={s.wrapInputCounter}>
                                <div className={s.resourceCounter}>
                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={less}
                                    onClick={() =>
                                      this.updateResourceCount(
                                        d.name,
                                        "archive",
                                        d.new_archive_count - 1
                                      )
                                    }
                                    hoverSmall
                                  />
                                  <input
                                    onKeyDown={e => this.handleArrows(d, "archive", e)}
                                    onBlur={() => this.validateResourceCount("archive")}
                                    value={d.new_archive_count}
                                    onChange={e =>
                                      this.updateResourceCount(
                                        d.name,
                                        "archive",
                                        e.currentTarget.value,
                                        false
                                      )
                                    }
                                  />
                                  <ButtonIcon
                                    className={s.btnResourceCounter}
                                    icon={plus}
                                    onClick={() =>
                                      this.updateResourceCount(
                                        d.name,
                                        "archive",
                                        d.new_archive_count + 1
                                      )
                                    }
                                    hoverSmall
                                  />
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        )}

                        <div className={s.estimate}>
                          <div>{t("order.label.first.bill.annual")}</div>

                          {isUpgradingDomain && (
                            <div className={s.estimateValue}>
                              {this.state.estimate ? (
                                <div className={s.totalEstimate}>{this.state.estimate} €</div>
                              ) : (
                                <Button
                                  className={s.estamateBtn}
                                  loading={this.state.esitmating}
                                  color="accent"
                                  onClick={() => this.calcEstimateWHMCS(d)}
                                >
                                  orders.show.calc.whmcs.total
                                </Button>
                              )}
                            </div>
                          )}

                          {isAddingDomain && <div className={s.estimateValue}>{d.estimate} €</div>}
                        </div>

                        <div className={s.infoEstimate}>
                          <TranslatedString>order.fee.priceVat</TranslatedString>
                        </div>

                        {isAddingDomain && domains.length > 1 && (
                          <div
                            className={s.deleteDomainBtn}
                            onClick={() => this.removeDomain(d.code)}
                          >
                            {t("button.delete.domain")}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}

              {isAddingDomain && (
                <div className={cx("inputFull2", s.wrapAddDomainBtn)}>
                  <Button
                    className={s.addDomainBtn}
                    icon={plus}
                    color="outline"
                    onClick={this.addDomain}
                  >
                    button.addDomain
                  </Button>

                  {this.state.validatingDomain && (
                    <div className={s.infoAddDomina}>
                      {t("infoMsg.validation.domain.inPorgress")}
                    </div>
                  )}
                </div>
              )}
            </div>

            {(!domains || domains.length === 0) && (
              <div className="warningMsgFP">
                <WarningBanner
                  color="yellow"
                  msg={<TranslatedString>infoMsg.order.minimum.annual.domain</TranslatedString>}
                />
              </div>
            )}

            {isAddingDomain && totalEmails < 5 && (
              <div className="warningMsgFP">
                <WarningBanner
                  color="yellow"
                  msg={<TranslatedString>infoMsg.order.minimum.annual</TranslatedString>}
                />
              </div>
            )}

            <div className="footerFP">
              <Button color="text" type="button" onClick={this.props.backToList}>
                button.cancel
              </Button>
              <Button
                color="accent"
                loading={this.state.esitmating || this.state.submitting}
                disabled={disabledButton}
                onClick={this.goToNextStep}
              >
                button.continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

EditOrder.contextTypes = ContextTypes;
